import * as PIXI from 'pixi.js';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import { TypedParentMixin } from '../../parentMixin';
import { TypedSerializableMixin } from '../../serializableMixin';
import {
    AlphaAttribute,
    BooleanAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute
} from '../../attributes';
import { DynamicYAlign, DynamicLevel, DynamicXAlign } from '../../../enums';
import { int2rgba } from '../../../libs/videoData';
import type { DynamicGraphicType } from '../types';
import type { CanvasTextDynamicGraphicRendererSettingsBase } from '../renderer/CanvasTextDynamicGraphic';
import { CanvasTextAssetDynamicAnimation } from './CanvasTextAssetDynamicAnimation';

/**
 * CanvasTextAssetDynamicGraphic is the Luminary equivalent of CanvasTextDynamicGraphic and provides a bridge between Luminary
 * and the CanvasText library.
 */
export abstract class CanvasTextAssetDynamicGraphic extends TypedUpdateFlowMixin(TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter))) {
    override _classType = 'CanvasTextDynamicGraphic';

    abstract readonly type: DynamicGraphicType;
    @BooleanAttribute()
    enabled = true;
    // TODO: implement asMask behaviour. This is currently not supported. We only want to use a mask as a mask if this
    //  property is set to true. Otherwise we want to use it as a color graphic. This is so the user can see the shape
    //  of the graphic in the editor. Note, we only want to deal with this on the bridge level. Our canvas API does not
    //  need to be aware of this.
    @BooleanAttribute()
    asMask = false;
    @StringAttribute({ choices: Object.values(DynamicLevel) })
    level = DynamicLevel.object;
    @ColorAttribute()
    fill = 0xFF00FF;
    @AlphaAttribute()
    fillAlpha = 0.5;
    @StringAttribute({ choices: Object.values(DynamicXAlign) })
    alignX = DynamicXAlign.left;
    @StringAttribute({ choices: Object.values(DynamicYAlign) })
    alignY = DynamicYAlign.top;
    @NumberAttribute()
    positionX = 0;
    @NumberAttribute()
    positionY = 0;

    #referenceToOutputMultiplier = 1;

    #animationConfigurations: CanvasTextAssetDynamicAnimation[] = [];

    constructor(settings) {
        super(settings);

        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.#referenceToOutputMultiplier;
    }

    getCanvasRendererSettings(): CanvasTextDynamicGraphicRendererSettingsBase {
        // convert asset color to canvas color
        const fill = int2rgba(this.fill, this.fillAlpha);

        // convert asset animations to canvas animations
        const animationConfigurations = this.#animationConfigurations
            .map(animationConfiguration => animationConfiguration.getCanvasRendererSettings());

        return {
            type: this.type,
            enabled: this.enabled,
            level: this.level,
            fill,
            alignX: this.alignX,
            alignY: this.alignY,
            positionX: this.positionX * this.referenceToOutputMultiplier,
            positionY: this.positionY * this.referenceToOutputMultiplier,
            animationConfigurations: animationConfigurations,
            asMask: this.asMask,
        };
    }

    get animationConfigurations() {
        return this.#animationConfigurations;
    }

    _animationConfigurationsCustomLoad(properties: Record<string, any>[]) {
        if (!properties) {
            return;
        }
        this.#animationConfigurations.splice(properties.length);
        properties.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    /**
     * Adds a CanvasTextAssetDynamicAnimation to the `animation` list.
     * The added configurator can be configured to animate any attribute on the asset.
     */
    addAnimation() {
        const animation = new CanvasTextAssetDynamicAnimation({
            parent: this,
            updateParentOnUpdate: true,
        });

        // do not mutate, we need to create a new reference for React
        this.#animationConfigurations = [...this.#animationConfigurations, animation];
        this.requiresUpdate = true;

        return animation;
    }

    removeAnimation(index: number) {
        // do not mutate, we need to create a new reference for React
        const animation = [...this.#animationConfigurations];
        animation.splice(index, 1);
        this.#animationConfigurations = animation;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize() {
        // why does getAttributesToSerialize(this) not work?
        return [
            'animationConfigurations',
            'enabled',
            'asMask',
            'level',
            'fill',
            'fillAlpha',
            'alignX',
            'alignY',
            'positionX',
            'positionY',
        ];
    }

    /** The list of properties that can be animated during draw */
    abstract get animatableProperties(): string[];

    /** The list of geometry properties that can be animated during draw. */
    abstract get animatableGeometricProperties(): string[];

    override get readonlyProperties() {
        return [
            'type',
        ];
    }

    loadTextAssetStyle(settings) {
        this.alignX = settings.alignGraphicX;
        this.alignY = settings.alignGraphicY;
        this.asMask = settings.asMask;
        this.level = DynamicLevel[settings.level];
        this.positionX = settings.shape.positionX;
        this.positionY = settings.shape.positionY;
        if (settings.shape.fillColor !== undefined) {
            this.fill = settings.shape.fillColor;
        }
        if (settings.shape.fillAlpha !== undefined) {
            this.fillAlpha = settings.shape.fillAlpha;
        }
        this.#animationConfigurations.splice(settings.animation.length);
        settings.animation.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.loadTextAssetStyle(data);
        });

        // For object level, baseline used to be the same as bottom. For canvas it now actually uses the baseline (of
        // the first line) so we convert it to maintain previous behaviour. We probably want to remove baseline
        // from the options in this case (LU-3303).
        if (this.level === DynamicLevel.object && this.alignY === DynamicYAlign.baseline) {
            this.alignY = DynamicYAlign.bottom;
        }
    }
}
